import { useState } from 'react';
import { useShowToast } from '@/hooks/useShowToast';
import {
  consentErrorMessage,
  isActiveApplicationsError,
} from './consentStatus';
import { useAnonymizeCandidate } from './api';
import { MarkConsentModal } from './MarkConsentModal';
import { AnonymizeModal } from './AnonymizeModal';
import type { ConsentTarget } from './types';

interface FlowProps {
  target: ConsentTarget;
  onClose: () => void;
}

// Mark-consent-manually flow for a table-row / More-menu modal slot.
// List rows carry no consent status, so mark_granted is always attempted
// first; the BE no-ops/errors it appropriately.
export const MarkConsentFlow = ({ target, onClose }: FlowProps) => (
  <MarkConsentModal open isGranted={false} target={target} onClose={onClose} />
);

// Anonymize flow incl. the confirm-retry pass for active applications
export const AnonymizeConsentFlow = ({ target, onClose }: FlowProps) => {
  const showToast = useShowToast();
  const anonymize = useAnonymizeCandidate();
  const [needsActiveConfirm, setNeedsActiveConfirm] = useState(false);

  const handleConfirm = () =>
    anonymize.mutate(
      { target, confirm: needsActiveConfirm || undefined },
      {
        onSuccess: () => {
          showToast({ title: 'Anonymization started', status: 'success' });
          onClose();
        },
        onError: (err) => {
          if (isActiveApplicationsError(err)) {
            setNeedsActiveConfirm(true);
            return;
          }
          showToast({ title: consentErrorMessage(err), status: 'error' });
        },
      }
    );

  return (
    <AnonymizeModal
      open
      requiresActiveConfirm={needsActiveConfirm}
      onClose={onClose}
      onConfirm={handleConfirm}
      isSubmitting={anonymize.isPending}
    />
  );
};
